/**
 * API Service
 * 
 * This file contains the base API configuration and common request methods.
 * It handles authentication, error handling, and request/response interceptors.
 */

import axios from 'axios';
import config from '../config/environment';
import { saveReturnTo } from '../utils/returnTo';

/**
 * Create axios instance with base configuration
 */
const api = axios.create({
  baseURL: config.API_BASE_URL,
  timeout: config.API_TIMEOUT,
  withCredentials: true,  // Add this line
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor to add authentication token
 */
api.interceptors.request.use(
  (requestConfig) => {
    const token = localStorage.getItem(config.TOKEN_STORAGE_KEY) || 
                  sessionStorage.getItem(config.TOKEN_STORAGE_KEY);
    if (token) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log requests in development
    if (config.DEV.ENABLE_LOGGING) {
      console.log('API Request:', {
        method: requestConfig.method?.toUpperCase(),
        url: requestConfig.url,
        data: requestConfig.data,
      });
    }
    
    return requestConfig;
  },
  (error) => {
    if (config.DEV.ENABLE_LOGGING) {
      console.error('API Request Error:', error);
    }
    return Promise.reject(error);
  }
);

/**
 * Response interceptor to handle common errors
 */
api.interceptors.response.use(
  (response) => {
    // Log responses in development
    if (config.DEV.ENABLE_LOGGING) {
      console.log('API Response:', {
        status: response.status,
        url: response.config.url,
        data: response.data,
      });
    }
    
    return response;
  },
  (error) => {
    if (config.DEV.ENABLE_LOGGING) {
      console.error('API Response Error:', error);
    }
    
    // Handle common error scenarios
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          // Unauthorized - only redirect if not a login attempt
          if (!error.config.url.includes('/auth/login') && !error.config.url.includes('/auth/profile')) {
            localStorage.removeItem(config.TOKEN_STORAGE_KEY);
            // Come back to this page after signing in again (sign-in pages are ignored).
            saveReturnTo(window.location.pathname + window.location.search);
            window.location.href = '/login';
          }
          break;
        case 403:
          // Forbidden - show access denied message
          console.error('Access denied');
          break;
        case 404:
          // Not found
          console.error('Resource not found');
          break;
        case 500:
          // Server error
          console.error('Server error');
          break;
        default:
          console.error('API Error:', data?.message || 'Unknown error');
      }
      
      // Return formatted error
      return Promise.reject({
        status,
        message: data?.detail || 'An error occurred',
        code: data?.code || 'UNKNOWN_ERROR',
      });
    } else if (error.request) {
      // Network error
      return Promise.reject({
        status: 0,
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
      });
    } else {
      // Other error
      return Promise.reject({
        status: 0,
        message: error.message || 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR',
      });
    }
  }
);

/**
 * Generic API request methods
 */
export const apiService = {
  /**
   * GET request
   * @param {string} url - The endpoint URL
   * @param {object} paramsOrConfig - Either query params OR an axios config object ({ params, headers, ... })
   * @returns {Promise} - Axios response promise
   */
  get: (url, paramsOrConfig = {}) => {
    // Backwards compatible:
    // - old usage: apiService.get(url, { page: 1, page_size: 25 })
    // - axios-style: apiService.get(url, { params: {...}, headers: {...} })
    const looksLikeAxiosConfig =
      paramsOrConfig &&
      typeof paramsOrConfig === 'object' &&
      (Object.prototype.hasOwnProperty.call(paramsOrConfig, 'params') ||
        Object.prototype.hasOwnProperty.call(paramsOrConfig, 'headers') ||
        Object.prototype.hasOwnProperty.call(paramsOrConfig, 'timeout') ||
        Object.prototype.hasOwnProperty.call(paramsOrConfig, 'responseType'));

    if (looksLikeAxiosConfig) {
      return api.get(url, paramsOrConfig);
    }

    return api.get(url, { params: paramsOrConfig });
  },

  /**
   * POST request
   * @param {string} url - The endpoint URL
   * @param {object} data - Request body data
   * @param {object} config - Additional axios config (headers, etc.)
   * @returns {Promise} - Axios response promise
   */
  post: (url, data = {}, config={}) => {
    return api.post(url, data, config);
  },

  /**
   * PUT request
   * @param {string} url - The endpoint URL
   * @param {object} data - Request body data
   * @returns {Promise} - Axios response promise
   */
  put: (url, data = {}) => {
    return api.put(url, data);
  },

  /**
   * PATCH request
   * @param {string} url - The endpoint URL
   * @param {object} data - Request body data
   * @returns {Promise} - Axios response promise
   */
  patch: (url, data = {}) => {
    return api.patch(url, data);
  },

  /**
   * DELETE request
   * @param {string} url - The endpoint URL
   * @returns {Promise} - Axios response promise
   */
  delete: (url) => {
    return api.delete(url);
  },

  /**
   * Upload file
   * @param {string} url - The endpoint URL
   * @param {FormData} formData - Form data with file
   * @param {function} onUploadProgress - Progress callback
   * @returns {Promise} - Axios response promise
   */
  upload: (url, formData, onUploadProgress = null) => {
    return api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });
  },
};

/**
 * Authentication helper methods
 */
export const authService = {
  /**
   * Set authentication token
   * @param {string} token - JWT token
   */
  setToken: (token) => {
    localStorage.setItem(config.TOKEN_STORAGE_KEY, token);
  },

  /**
   * Get authentication token
   * @returns {string|null} - JWT token or null
   */
  getToken: () => {
    return localStorage.getItem(config.TOKEN_STORAGE_KEY);
  },

  /**
   * Remove authentication token
   */
  removeToken: () => {
    localStorage.removeItem(config.TOKEN_STORAGE_KEY);
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} - True if authenticated
   */
  isAuthenticated: () => {
    const token = authService.getToken();
    return !!token;
  },
};


export default api;
